import { bind } from 'decko';
import { inject } from 'dependable-react';
import { css } from 'emotion';
import camelCase from 'lodash/camelCase';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { runInAction } from 'mobx';
import closeSvg from '../assets/close.svg';
import { BREAKPOINT_MEDIUM } from '../consts/breakpoints';
import { Avatar } from '../state/models';
import { Form } from './Form';

import background from '../assets/avatar-preview.jpg';
import { APP_DATA_TOKEN, I18N_TOKEN } from '../services/InjectTokens';
import { getPairedImage } from '../utils/getPairedImage';
import { Select } from './Select';
import { pushGtmData } from '../utils/gtm';

const main = css`
  position: absolute;
  top: 60px;
  left: 0;
  right: 0;

  background: white;
  z-index: 501;
  padding-bottom: 65px;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    position: fixed;
    top: 80px;
    bottom: 0;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 0;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 65px;
    background-image: linear-gradient(to bottom, rgba(226, 226, 226, 0), rgba(193, 193, 193, 0.37));

    @media (max-width: ${BREAKPOINT_MEDIUM}) {
      display: none;
      height: 0;
    }
  }
`;

const content = css`
  max-width: 950px;
  margin: 0 auto;
  padding-bottom: 20px;
  @media (min-width: ${BREAKPOINT_MEDIUM}) {
    margin: 20px auto 0;
    padding: 0 20px;
  }
`;

const preview = css`
  flex: 1;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    flex: auto;
    background: url(${background});
    background-size: cover;
  }
`;

const previewAvatar = (hasPreview: boolean) => css`
  position: relative;
  height: 500px;
  margin: 0 auto;
  flex: 1;
  background: #fff;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    display: ${hasPreview ? 'block' : 'none'};
    flex: auto;
    max-width: 300px;
    width: 100%;
    height: 300px;
    margin: 20px auto;
    border-radius: 5px;
    box-shadow: 1px 1px 25px 1px rgba(0, 0, 0, 0.16);
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    height: 100%;

    @media (max-width: ${BREAKPOINT_MEDIUM}) {
      top: 50%;
      left: 50%;

      transform: translate(-50%, -50%);
    }
  }
`;

const close = css`
  display: block;
  position: absolute;
  top: 10px;
  right: 30px;
  background: url(${closeSvg});
  height: 20px;
  width: 20px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
`;

interface ICharbuilderProps {
  bookId?: number;
  avatar: Avatar;
  isVisible: boolean;
  currentLang: string;
  onChangeLang(lang: string): void;
  onSave(avatar: Avatar, data: Record<string, string>, mainCharacter?: boolean): void;
  onClose(): void;
}

interface IState {
  charParams: Record<string, any>;
}

@observer
export class Charbuilder extends React.Component<ICharbuilderProps> {
  private i18n = inject(I18N_TOKEN);
  private appState = inject(APP_DATA_TOKEN);

  constructor(props: any) {
    super(props);

    let langOverride: string;

    const params = new URLSearchParams(window.location.search);
    if (params.get('lang')) {
      langOverride = params.get('lang')!;
      //
      runInAction(() => {
        this.props.onChangeLang(langOverride);
      });
    }
  }

  @observable
  private componentState: IState = {
    charParams: this.props.avatar.params || this.props.avatar.defaultPreview,
  };

  @computed
  private get charParams() {
    return this.componentState.charParams;
  }

  @computed
  private get isPreviewDisplaying() {
    return Boolean(this.generateImages.length);
  }

  public render() {
    const { bookId, avatar, onSave, onClose, isVisible, currentLang } = this.props;
    if (!isVisible) {
      return null;
    }
    pushGtmData({ event: 'personalizacija', korak: { items: [{ step: `step_name_${avatar.title}` }] } });

    return (
      <div className={main}>
        <div className={content}>
          <div role="button" onClick={onClose} className={close} />
          <Form
            bookId={bookId}
            avatar={avatar}
            onSave={onSave}
            defaults={this.props.avatar.defaultPreview}
            onFeatureChange={this.onFeatureChange}
            elementsAfter={
              avatar.mainCharacter && (
                <Select
                  selected={currentLang}
                  onChange={this.onChangeLang}
                  values={[
                    { name: this.i18n.t('language_picker') },
                    ...this.appState.languages.map((language) => ({
                      name: this.i18n.resolveLanguageTag(language),
                      value: language,
                    })),
                  ]}
                />
              )
            }
          >
            <div className={preview}>
              <div className={previewAvatar(this.isPreviewDisplaying)}>
                {this.generateImages.map((img, i) => (
                  <img
                    src={img.src}
                    alt="Avatar"
                    key={`character-body-${i.toString()}`}
                    style={{ zIndex: img.zIndex, display: img.display }}
                  />
                ))}
              </div>
            </div>
          </Form>
        </div>
      </div>
    );
  }

  @bind
  private onChangeLang(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value;
    if (value) {
      this.props.onChangeLang(e.target.value);
    }
  }

  @bind
  @action
  private onFeatureChange(params: any) {
    let glasses;
    const { avatar } = this.props;

    if (params.uid === 'sex') {
      const gender = params.value.uid === 'm' ? 2 : 3;
      avatar.genderId = gender;
      if (avatar.params && avatar.params.hairStyle) {
        const { hairStyle } = avatar.previewImages.find((previewImage: any) => {
          if (previewImage.genderId === 1 && previewImage.hairStyle) {
            return previewImage.hairStyle && previewImage.genderId === 1;
          }

          return previewImage.hairStyle && previewImage.genderId === gender;
        });

        this.componentState.charParams.hairStyle = hairStyle;
      }
    }

    if (params.uid === 'glasses') {
      glasses = params.value.pickerValue;
    }

    this.componentState.charParams = {
      ...this.componentState.charParams,
      [camelCase(params.uid)]: params.value.uid,
      ...(glasses !== undefined && { glasses }),
    };

    avatar.params = {
      ...avatar.params,
      ...this.componentState.charParams,
    };
  }

  @bind
  private generateZIndex(key: string) {
    switch (key) {
      case 'glasses':
      case 'birthday':
      case 'stuffed_animal':
        return 7;
      case 'eyeColor':
      case 'hair':
      case 'beard':
        return 6;
      default:
        return 4;
    }
  }

  @computed
  private get generateImages() {
    const { params } = this.props.avatar;
    let genderId: number;

    if (params && params.sex) {
      genderId = params.sex === 'm' ? 2 : 3;
    } else {
      genderId = this.props.avatar.genderId;
    }
    const avatarParams = {
      ...params,
      ...this.charParams,
    };

    return Object.keys(avatarParams)
      .map((key) => {
        const { hairColor, hairStyle, beardColor, beardStyle, eyeColor, eyeShape, sex, sexRaceType } = avatarParams;

        const hairImage = getPairedImage(
          this.props.avatar,
          { name: 'hairColor', value: hairColor },
          { name: 'hairStyle', value: hairStyle },
        );

        const beardImage = getPairedImage(
          this.props.avatar,
          { name: 'beardColor', value: beardColor },
          { name: 'beardStyle', value: beardStyle },
        );

        const eyeImage = getPairedImage(
          this.props.avatar,
          { name: 'eyeColor', value: eyeColor },
          { name: 'eyeShape', value: eyeShape },
        );

        const raceImage = getPairedImage(
          this.props.avatar,
          { name: 'sex', value: sex },
          { name: 'sexRaceType', value: sexRaceType },
        );

        const otherImages = this.props.avatar.previewImages
          .filter(
            (obj: any) =>
              !Object.keys(obj).find(
                (previewKey) => previewKey === 'sexRaceType' || previewKey === 'beard' || previewKey === 'hair',
              ),
          )
          .find((obj: any) => {
            if (obj.genderId === 1) {
              return obj[key] === avatarParams[key] && obj.genderId === 1;
            }

            return obj[key] === avatarParams[key] && obj.genderId === genderId;
          });

        if (key.includes('sexRaceType')) {
          return {
            display: raceImage ? 'block' : 'none',
            src: raceImage && raceImage.image.smallUrl,
            zIndex: this.generateZIndex('race'),
          };
        }

        if (key.includes('hair')) {
          return {
            display: hairImage ? 'block' : 'none',
            src: hairImage && hairImage.image.smallUrl,
            zIndex: this.generateZIndex('hair'),
          };
        }

        if (key.includes('eye') && eyeImage) {
          return {
            display: eyeImage ? 'block' : 'none',
            src: eyeImage && eyeImage.image.smallUrl,
            zIndex: this.generateZIndex('hair'),
          };
        }

        if (key.includes('beard')) {
          return {
            display: beardImage ? 'block' : 'none',
            src: beardImage && beardImage.image.smallUrl,
            zIndex: this.generateZIndex('beard'),
          };
        }

        if (key.includes('stuffed')) {
          return {
            display: otherImages ? 'block' : 'none',
            src: otherImages && otherImages.image.smallUrl,
            zIndex: this.generateZIndex('stuffed_animal'),
          };
        }

        return {
          display: otherImages ? 'block' : 'none',
          src: otherImages && otherImages.image.smallUrl,
          zIndex: this.generateZIndex(key),
        };
      })
      .filter(({ src }) => Boolean(src));
  }
}
